import { Link } from "react-router-dom";
import type { Campaign } from "../api/types";
import {
  GROUPS, LIST_UNKNOWN, PRESETS, criteriaIn, isSet, listKeepsUnknown, optionLabel, presetIsOn, rankingFor,
  rankingOf, readsAsOptions, yearOf,
  type Filters, type FilterValue, type Weights,
} from "../lib/campaignFilters";

/* What each measure in include_unknown is called on the tab. */
const KEPT_WORDS: Record<string, (f: Filters) => string> = {
  units: () => "unit count",
  stories: () => "stories",
  year: (f) => yearOf(f.year_of).label.toLowerCase(),
  portfolio: () => "properties owned",
  rate: () => "incentive rate",
};
import { limitsWords, useCutPreview, useEnrichmentChoices } from "./EnrichmentOptions";
import { useStageWords } from "./StageWords";

/* What a campaign selects and how it ranks, as a person reads it: the
 * segment, then each group's criteria as chips in the builder's own words,
 * then the ranking, who to reach and the enrichment option. Edit criteria,
 * among the page's actions, opens the builder with all of it.
 *
 * It replaces a table of the engine's filter keys beside their values.
 * docs/plans/campaigns-overhaul.md, The Criteria tab. */
export function CampaignCriteria({ token, c, editPath }: { token: string; c: Campaign; editPath: string }) {
  const filters = (c.filters ?? {}) as Filters;
  const choices = useEnrichmentChoices(token);
  const stops = useCutPreview(token, c.name, { perRole: c.contact_per_role ?? null, perCompany: c.contact_per_company ?? null,
                                               reachFill: c.contact_reach_fill ?? true });
  const at = (stops.data ?? []).find((s) => s.cut === (c.contact_cut_in_use ?? -1));
  const segment = PRESETS.find((p) => presetIsOn(filters, p));
  const ranking = rankingFor(c.weights as Weights | null);
  const set = Object.keys(filters).filter((k) => isSet(filters[k]));

  const level = (l: string) => choices.data?.levels.find((x) => x.level === l)?.word ?? l;

  return (
    <section className="rp-tabbody cc" aria-label="Criteria">
      <p className="cc-segment">
        <strong>{segment ? segment.label : set.length ? "Custom" : "No criteria"}</strong>
        <Link to={editPath} className="cc-edit">Edit criteria</Link>
      </p>
      {!set.length ? (
        <p className="refusal">No criteria. This campaign matches every property.</p>
      ) : (
        <CriteriaGroups token={token} filters={filters} />
      )}

      <dl className="cc-groups cc-more">
        <div className="cc-group">
          <dt>Ranked by</dt>
          <dd>
            {ranking === "engine" ? "Weights set in the engine."
              : ranking === "none" ? "Not ranked. Listed by unit count."
              : <><strong>{rankingOf(ranking).label}.</strong> {rankingOf(ranking).says}</>}
          </dd>
        </div>
        <div className="cc-group">
          <dt>Contacts</dt>
          <dd>
            {(c.roles ?? []).length ? (c.roles ?? []).join(", ") : "Each property's own order"}
            {(c.seniorities ?? []).length ? <> · {(c.seniorities ?? []).map(level).join(", ")}</> : null}
            {c.contact_state ? <> · in {c.contact_state}</> : <> · anywhere</>}
            <br />
            Minimum fit {c.contact_cut_in_use ?? ""}{c.contact_cut === null || c.contact_cut === undefined ? ", the default" : ""}
            {at?.words ? <span className={at.in_red ? "refusal" : "muted"}>: {at.words}</span> : null}
            <br />
            {limitsWords(c.contact_per_role, c.contact_per_company)}
          </dd>
        </div>
        <div className="cc-group">
          <dt>Enrichment</dt>
          <dd>Automatic web enrichment {c.auto_enrich ? "on" : "off"}</dd>
        </div>
        {c.description ? (
          <div className="cc-group">
            <dt>Description</dt>
            <dd>{c.description}</dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

/** A campaign's criteria as chips in the builder's words, one line per
 *  group. The Criteria tab shows it, and so does Compare with a campaign,
 *  above its breakdown (James, 2026-10-23). */
export function CriteriaGroups({ token, filters }: { token: string; filters: Filters }) {
  const words = useStageWords(token);
  const set = Object.keys(filters).filter((k) => isSet(filters[k]));

  const said = (key: string, v: FilterValue): string => {
    if (key === "year_of") return yearOf(v).label;
    if (key === "include_unknown" && Array.isArray(v)) {
      return "included for " + v.map((m) => (KEPT_WORDS[m] ? KEPT_WORDS[m](filters) : String(m))).join(", ");
    }
    if (key === "project_status_in" && Array.isArray(v) && words.data) {
      const have = new Set(v as string[]);
      const whole = words.data.filter((w) => w.statuses.every((s) => have.has(s))).map((w) => w.word);
      const covered = new Set(words.data.filter((w) => whole.includes(w.word)).flatMap((w) => w.statuses));
      const rest = (v as string[]).filter((s) => !covered.has(s)).map(optionLabel);
      return [...whole, ...rest].join(", ");
    }
    // A list whose value can be not known says whether those properties are
    // in, in the words of the box that set it (James, 2026-10-22).
    const u = LIST_UNKNOWN[key];
    if (u && Array.isArray(v)) {
      const known = (v as string[]).filter((x) => x !== u.value).map((x) => optionLabel(String(x))).join(", ");
      if (!known) return `Only properties with ${u.none}`;
      return listKeepsUnknown(filters, key) ? `${known}, and properties with ${u.none}`
        : `${known}, not properties with ${u.none}`;
    }
    if (v === true) return "Yes";
    if (v === false) return "No";
    if (Array.isArray(v)) return (readsAsOptions(key) ? v.map((x) => optionLabel(String(x))) : v).join(", ");
    return String(v);
  };

  return (
    <dl className="cc-groups">
      {GROUPS.map((g) => {
        const here = criteriaIn(g).filter((cr) => isSet(filters[cr.key]));
        if (!here.length) return null;
        return (
          <div className="cc-group" key={g}>
            <dt>{g}</dt>
            <dd>
              {here.map((cr) => (
                <span className="chip" key={cr.key}>
                  {cr.label}: {said(cr.key, filters[cr.key] as FilterValue)}
                </span>
              ))}
            </dd>
          </div>
        );
      })}
      {set.filter((k) => !GROUPS.some((g) => criteriaIn(g).some((cr) => cr.key === k))).length ? (
        <div className="cc-group">
          <dt>Other</dt>
          <dd>
            {set.filter((k) => !GROUPS.some((g) => criteriaIn(g).some((cr) => cr.key === k))).map((k) => (
              <span className="chip" key={k}>{k.replace(/_/g, " ")}: {said(k, filters[k] as FilterValue)}</span>
            ))}
          </dd>
        </div>
      ) : null}
    </dl>
  );
}
